import { useEffect, useState } from 'react';
import { Mail, Ticket, User } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Sheet } from '@/components/ui/Sheet';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { AiActionButton } from '@/components/ui/AiActionButton';
import { InlineEdit } from '@/components/ui/InlineEdit';
import { InlineSelect } from '@/components/ui/InlineSelect';
import { SentimentIndicator } from '@/components/modules/SentimentIndicator';
import { useContactsStore } from '@/stores/contactsStore';
import { api } from '@/lib/api';
import type { ApiResponse } from '@nexus/shared/types';
import { toast } from '@/stores/toastStore';
import { formatRelative } from '@/lib/format';
import type { ContactType } from '@nexus/shared/types';

const typeVariant: Record<string, 'success' | 'info' | 'purple' | 'warning' | 'default'> = {
  customer: 'success',
  lead: 'info',
  partner: 'purple',
  vendor: 'warning',
  internal: 'default',
  other: 'default',
};

const contactTypeOptions = [
  { value: 'lead', label: 'Lead' },
  { value: 'customer', label: 'Customer' },
  { value: 'partner', label: 'Partner' },
  { value: 'vendor', label: 'Vendor' },
  { value: 'internal', label: 'Internal' },
  { value: 'other', label: 'Other' },
];

interface ContactSheetProps {
  id: string | null;
  open: boolean;
  onClose: () => void;
}

export function ContactSheet({ id, open, onClose }: ContactSheetProps) {
  const navigate = useNavigate();
  const { selected, fetchOne, update, clearSelected } = useContactsStore();

  const [aiAnalysis, setAiAnalysis] = useState<{ sentiment: string; summary: string } | null>(null);
  const [aiAnalyzing, setAiAnalyzing] = useState(false);

  useEffect(() => {
    if (id && open) {
      fetchOne(id);
      setAiAnalysis(null);
    }
    return () => { clearSelected(); };
  }, [id, open, fetchOne, clearSelected]);

  const handleAiAnalyze = async () => {
    if (!id) return;
    setAiAnalyzing(true);
    try {
      const res = await api.post<ApiResponse<{ sentiment: string; summary: string }>>(`/api/contacts/${id}/analyze`);
      if (res.data) {
        setAiAnalysis(res.data);
        // Also update the contact's sentiment in the store
        await update(id, { sentiment: res.data.sentiment });
      }
    } catch {
      toast.error('AI Analysis failed', 'Check that an AI provider is configured in Settings.');
    } finally {
      setAiAnalyzing(false);
    }
  };

  const contact = selected;
  if (!contact) {
    return (
      <Sheet open={open} onClose={onClose} title="Contact">
        <div className="flex items-center justify-center py-12 text-surface-500">Loading...</div>
      </Sheet>
    );
  }

  const initials = contact.name
    .split(' ')
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  return (
    <Sheet open={open} onClose={onClose} title="Contact Details" width="lg">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-start gap-4">
          <div className="h-12 w-12 rounded-full bg-nexus-600/20 flex items-center justify-center text-nexus-400 font-semibold text-lg shrink-0">
            {initials}
          </div>
          <div className="flex-1 min-w-0">
            <InlineEdit
              value={contact.name}
              onSave={async (v) => { await update(contact.id, { name: v }); }}
              className="text-lg font-semibold"
            />
            <div className="mt-1">
              <InlineSelect
                value={contact.type}
                options={contactTypeOptions}
                onSave={async (v) => { await update(contact.id, { type: v as ContactType }); }}
              />
            </div>
          </div>
        </div>

        {/* Contact Info */}
        <div className="space-y-3">
          <h3 className="text-xs font-semibold text-surface-500 uppercase tracking-wider">Contact Info</h3>
          <div className="grid grid-cols-[100px_1fr] gap-y-2 items-center">
            <span className="text-xs text-surface-500">Email</span>
            <InlineEdit
              value={contact.email || ''}
              placeholder="Add email"
              onSave={async (v) => { await update(contact.id, { email: v || null }); }}
            />
            <span className="text-xs text-surface-500">Phone</span>
            <InlineEdit
              value={contact.phone || ''}
              placeholder="Add phone"
              onSave={async (v) => { await update(contact.id, { phone: v || null }); }}
            />
          </div>
        </div>

        {/* Scores */}
        <div className="space-y-3">
          <h3 className="text-xs font-semibold text-surface-500 uppercase tracking-wider">Insights</h3>
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-surface-800 rounded-lg p-3 border border-surface-700">
              <p className="text-xs text-surface-500 mb-1">Lead Score</p>
              <div className="flex items-center gap-2">
                <div className="flex-1 h-1.5 bg-surface-700 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-nexus-500 rounded-full"
                    style={{ width: `${contact.leadScore ?? 0}%` }}
                  />
                </div>
                <span className="text-sm font-medium text-surface-200">{contact.leadScore ?? 0}</span>
              </div>
            </div>
            <div className="bg-surface-800 rounded-lg p-3 border border-surface-700">
              <p className="text-xs text-surface-500 mb-1">Churn Risk</p>
              <div className="flex items-center gap-2">
                <div className="flex-1 h-1.5 bg-surface-700 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-red-500 rounded-full"
                    style={{ width: `${contact.churnRisk ?? 0}%` }}
                  />
                </div>
                <span className="text-sm font-medium text-surface-200">{contact.churnRisk ?? 0}</span>
              </div>
            </div>
            <div className="bg-surface-800 rounded-lg p-3 border border-surface-700">
              <p className="text-xs text-surface-500 mb-1">Interactions</p>
              <p className="text-lg font-semibold text-surface-200">{contact.totalInteractions}</p>
            </div>
          </div>
        </div>

        {/* Sentiment + AI Analyze */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-xs text-surface-500">Sentiment</span>
              {contact.sentiment && <SentimentIndicator sentiment={contact.sentiment} showLabel />}
              {!contact.sentiment && <span className="text-xs text-surface-500 italic">Unknown</span>}
            </div>
            <AiActionButton
              size="sm"
              label="AI Analyze"
              onClick={handleAiAnalyze}
              loading={aiAnalyzing}
            />
          </div>
          {aiAnalysis && (
            <div className="p-3 bg-surface-900/80 border border-nexus-600/30 rounded-lg space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-xs font-medium text-nexus-400">AI Analysis</p>
                <Badge variant="info">{aiAnalysis.sentiment}</Badge>
              </div>
              <p className="text-sm text-surface-300">{aiAnalysis.summary}</p>
            </div>
          )}
        </div>

        {/* Tags */}
        {contact.tags && contact.tags.length > 0 && (
          <div className="space-y-2">
            <h3 className="text-xs font-semibold text-surface-500 uppercase tracking-wider">Tags</h3>
            <div className="flex flex-wrap gap-1.5">
              {contact.tags.map((tag) => (
                <Badge key={tag} variant="default">{tag}</Badge>
              ))}
            </div>
          </div>
        )}

        {/* Metadata */}
        <div className="flex items-center gap-4 text-xs text-surface-500">
          {contact.lastInteractionAt && (
            <span>Last interaction: {formatRelative(contact.lastInteractionAt)}</span>
          )}
          <span>Updated: {formatRelative(contact.updatedAt)}</span>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 pt-2 border-t border-surface-700">
          <Button
            variant="secondary"
            size="sm"
            icon={<Mail className="h-3.5 w-3.5" />}
            onClick={() => { onClose(); navigate('/email'); }}
          >
            Send Email
          </Button>
          <Button
            variant="secondary"
            size="sm"
            icon={<Ticket className="h-3.5 w-3.5" />}
            onClick={() => { onClose(); navigate('/support'); }}
          >
            Create Ticket
          </Button>
          <Button
            variant="ghost"
            size="sm"
            icon={<User className="h-3.5 w-3.5" />}
            onClick={() => { onClose(); navigate(`/contacts/${contact.id}`); }}
          >
            Full Profile
          </Button>
        </div>
      </div>
    </Sheet>
  );
}
